import { navigateToExternalLink } from '@utilities';
import AppRoutes from '@utilities/app-routes';
import { Image } from 'primereact/image';
import { Menu } from 'primereact/menu';
import React, { useEffect, useRef, useState } from 'react';
import { SidebarLeft } from 'iconsax-react';

export const UserDetails = ({ userName, userEmail, userPhoneNumber }: Record<string, any>) => (
	<div className="flex gap-2.5 flex-col">
		{userName && (
			<div className="flex items-center gap-2.5 text-sm">
				<i className="pi pi-user text-[var(--color-accent)] text-sm"></i>
				<span className="text-skin-base font-medium">{userName}</span>
			</div>
		)}
		{userEmail && (
			<div className="flex items-center gap-2.5 text-sm">
				<i className="pi pi-envelope text-[var(--color-accent)] text-sm"></i>
				<span className="text-skin-muted">{userEmail}</span>
			</div>
		)}
		{userPhoneNumber && (
			<div className="flex items-center gap-2.5 text-sm">
				<i className="pi pi-phone text-[var(--color-accent)] text-sm"></i>
				<span className="text-skin-muted">{userPhoneNumber}</span>
			</div>
		)}
	</div>
);

const GlobalNavbar = ({
	isUserLoading,
	user = {},
	logout,
	onMenuToggle,
	sidebarVisible = true
}: Record<string, any>) => {
	const menu: any = useRef(null);
	const [isDark, setIsDark] = useState(() => {
		if (typeof window !== 'undefined') {
			return localStorage.getItem('theme') === 'dark' || document.documentElement.classList.contains('dark');
		}
		return false;
	});

	useEffect(() => {
		if (isDark) {
			document.documentElement.classList.add('dark');
			localStorage.setItem('theme', 'dark');
		} else {
			document.documentElement.classList.remove('dark');
			localStorage.setItem('theme', 'light');
		}
	}, [isDark]);

	const dropdownItems = [
		{
			label: <UserDetails userName={user?.fullName} userEmail={user.email} userPhoneNumber={user.phoneNumber} />,
			command: () => navigateToExternalLink(AppRoutes.PROFILE)
		},
		{
			label: 'Organization',
			icon: 'pi pi-users',
			command: () => navigateToExternalLink(AppRoutes.ORGANIZATION)
		},
		{
			label: 'Logout',
			icon: 'pi pi-fw pi-power-off',
			command: logout
		}
	];

	return (
		<div
			className="h-[52px] sticky z-50 w-full bg-[var(--color-navbar-bg)] border-b border-[var(--color-navbar-border)]"
		>
			<div className="flex h-full items-center px-6 gap-2">
				{/* Hamburger — visible when sidebar is closed */}
				{onMenuToggle && !sidebarVisible && (
					<button
						className="flex items-center justify-center w-7 h-7 rounded-lg bg-skin-card border border-skin shadow-soft text-skin-muted hover:bg-skin-hover transition-all duration-200 cursor-pointer"
						onClick={onMenuToggle}
						aria-label="Open menu"
					>
						<SidebarLeft size={16} variant="Linear" color="currentColor" />
					</button>
				)}

				{/* Spacer to push actions to the right */}
				<div className="flex-1" />

				{/* Right side - actions */}
				<div className="flex items-center gap-1.5">
					{/* Dark/Light Mode Toggle */}
					<button
						className="flex items-center justify-center w-7 h-7 rounded-lg bg-skin-card border border-skin shadow-soft text-skin-muted hover:bg-skin-hover transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]"
						onClick={() => setIsDark(prev => !prev)}
						title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
						aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
					>
						<i
							className={`theme-toggle-icon pi ${isDark ? 'pi-sun text-[var(--color-warning)]' : 'pi-moon'} text-[16px]`}
							aria-hidden="true"
						></i>
					</button>

					{/* Separator */}
					<div className="hidden sm:block w-px h-5 bg-[var(--color-border)]" />

					{/* User avatar */}
					<div className="sm:flex items-center gap-2 hidden">
						{!!user.profilePicture ? (
							<Image
								src={!isUserLoading ? user?.profilePicture?.documentUrl : ''}
								onClick={event => menu.current.toggle(event)}
								pt={{
									root: {
										className:
											'rounded-[10px] overflow-hidden h-8 w-8 cursor-pointer bg-skin-surface border border-skin shadow-soft'
									},
									image: { className: 'h-8 w-8 object-cover' }
								}}
							/>
						) : (
							<button
								className="flex items-center justify-center w-8 h-8 rounded-[10px] cursor-pointer bg-[var(--color-accent)] text-[var(--color-text-inverted)] font-semibold text-sm border-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] shadow-soft"
								onClick={event => menu.current.toggle(event)}
								aria-label="Open user menu"
							>
								{user?.fullName?.charAt(0)?.toUpperCase() || (
									<i className="pi pi-user text-base" aria-hidden="true"></i>
								)}
							</button>
						)}
						<div className="hidden lg:flex flex-col">
							<span className="text-[13px] font-medium text-skin-base leading-tight">
								{user?.fullName}
							</span>
							<span className="text-[11px] text-skin-faint leading-tight">
								{user?.email}
							</span>
						</div>
					</div>
				</div>
			</div>
			<Menu
				ref={menu}
				popup
				model={dropdownItems}
				pt={{
					root: {
						className:
							'w-max p-1.5 border border-skin rounded-xl bg-skin-card shadow-overlay'
					},
					separator: { className: 'm-0' }
				}}
			/>
		</div>
	);
};

export default GlobalNavbar;
